import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './ForkliftScales.css';
import { useProducts, type ForkliftScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const ForkliftScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products, loading, error } = useProducts<ForkliftScalesSpecs>('forklift-scales', editMode);
  const navigate = useNavigate();
  const models = products;

  const applications = [
    {
      title: 'Almacenes',
      description: 'Pesaje rápido de mercancías durante carga y descarga de materiales.',
    },
    {
      title: 'Manufactura',
      description: 'Control de peso de materias primas y productos en proceso.',
    },
    {
      title: 'Logística',
      description: 'Verificación de peso de pallets y contenedores para envío.',
    },
    {
      title: 'Distribución',
      description: 'Control de inventario y verificación de cargas.',
    },
    {
      title: 'Reciclaje',
      description: 'Pesaje de materiales reciclables durante recolección.',
    },
    {
      title: 'Construcción',
      description: 'Pesaje de materiales de construcción y agregados.',
    },
  ];

  const advantages = [
    {
      title: 'Eficiencia Operativa',
      description: 'Elimina tiempo muerto permitiendo pesaje durante el transporte.',
    },
    {
      title: 'Sistema Desmontable',
      description: 'Fácil instalación y remoción según necesidades operativas.',
    },
    {
      title: 'Múltiples Capacidades',
      description: 'Opciones de 2,500 kg a 7,270 kg para diversas aplicaciones.',
    },
    {
      title: 'Flexibilidad',
      description: 'Múltiples tamaños de plataforma e indicadores compatibles.',
    },
    {
      title: 'Precisión',
      description: 'Sistema de 4 barras pesadoras para máxima exactitud.',
    },
    {
      title: 'Durabilidad',
      description: 'Construcción robusta para uso industrial intensivo.',
    },
  ];

  const optionalFeatures = [
    {
      title: 'Certificación',
      description: 'Opciones certificadas y no certificadas según requisitos.',
    },
    {
      title: 'Detección de Ángulo',
      description: 'Funcionalidad avanzada para compensación automática.',
    },
    {
      title: 'Tarjeta de Fecha y Hora',
      description: 'Opcional para indicador WI-125 para registro temporal.',
    },
    {
      title: 'Salida de Datos',
      description: 'Conectividad para integración con sistemas existentes.',
    },
  ];

  return (
    <div className="fks-page">
      {error && <div className="fks-status-bar fks-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="fks-hero">
        <div className="container fks-hero-content">
          <nav className="fks-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="fks-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="fks-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas de Montacargas</span>
          </nav>
          <h1 className="fks-hero-title">Básculas de Montacargas</h1>
          <p className="fks-hero-subtitle">
            Sistemas desmontables de pesaje que eliminan el tiempo muerto, permitiendo
            medir peso directamente en el montacargas.
          </p>
          {authType === 'internal' && (
            <button
              className={`fks-btn-edit-mode${editMode ? ' fks-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="fks-hero-image" aria-hidden="true">
          <img src="/images/forklift-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="fks-overview">
        <div className="container">
          <div className="fks-overview-grid">
            <div className="fks-overview-text">
              <span className="fks-label">Descripción general</span>
              <h2>Pesaje en movimiento sin tiempo muerto</h2>
              <p>
                Nuestras básculas de montacargas están diseñadas para eliminar el tiempo muerto
                de pesaje, permitiendo la medición de peso directamente en el montacargas. Son
                sistemas desmontables que ofrecen flexibilidad y eficiencia en operaciones
                industriales.
              </p>
              
            </div>
            <div>
              <ul className="fks-features-list" aria-label="Características principales">
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Estructura robusta de acero al carbón
                </li>
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Sistema de 4 barras pesadoras para precisión
                </li>
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Caja de conexiones de acero inoxidable
                </li>
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Cable retráctil para manejo seguro
                </li>
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Múltiples opciones de indicadores
                </li>
                <li>
                  <svg className="fks-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Configuraciones certificadas disponibles
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="fks-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/forklift-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.capacity ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.platformSizes?.length ? [{ icon: '📐', label: 'Plataformas', items: (model.specs?.platformSizes  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.compatibleIndicators?.length ? [{ icon: '🖥️', label: 'Indicadores', items: (model.specs?.compatibleIndicators  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="fks-applications">
        <div className="container">
          <span className="fks-label fks-label--light">Industrias</span>
          <h2 className="fks-section-title fks-section-title--light">Aplicaciones</h2>
          <div className="fks-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="fks-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="fks-advantages">
        <div className="container">
          <span className="fks-label">Beneficios</span>
          <h2 className="fks-section-title">Ventajas de nuestras básculas de montacargas</h2>
          <div className="fks-advantages-grid">
            {advantages.map((adv, index) => (
              <div key={index} className="fks-adv-card">
                <h3>{adv.title}</h3>
                <p>{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Optional features ────────────────────────────────── */}
      <section className="fks-configurations">
        <div className="container">
          <div className="fks-configurations-inner">
            <div className="fks-configurations-header">
              <span className="fks-label">Personalización</span>
              <h2 className="fks-section-title">Características opcionales</h2>
              <p>
                Adapte su báscula de montacargas a los requerimientos específicos
                de su operación.
              </p>
            </div>
            <div className="fks-options-grid">
              {optionalFeatures.map((feature, index) => (
                <div key={index} className="fks-option-card">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForkliftScales;
